import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { CiEdit, CiTrash, CiSquareChevLeft } from "react-icons/ci";
import { FaRegFileLines, FaSpinner } from "react-icons/fa6";
import Swal from "sweetalert2";
import "./firmalar.css";
import { toast } from "react-toastify";
import { nodeURL } from "../configUrl";
import FirmaCariModal from "./firmaCari/FirmaCariModal";

const birimler = [
  { value: "1", label: "₺" },
  { value: "2", label: "$" },
  { value: "3", label: "€" },
];

// Sunucudan gelen kiralama kalemini düzenleme state'ine çevirir. Sunucu açıklamayı
// "urun_aciklama" olarak döndürüyor; kaydederken "aciklama" beklendiği için
// burada eşleniyor. Bu eşleme olmazsa Güncelle açıklamaları boş gönderir.
const mapKiralamaUrun = (u) => ({
  ...u,
  aciklama: u.urun_aciklama ?? u.aciklama ?? "",
  _localKey: u.kiralama_urunler_id,
});

const newLocalKey = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`;

const KiralamaEditor = () => {
  const { kiralamaId } = useParams();
  const isEdit = !!kiralamaId;
  const navigate = useNavigate();
  const location = useLocation();
  const gelenTeklif = location.state?.teklifler;

  const [teklifInfo, setTeklifInfo] = useState(null);
  const [firms, setFirms] = useState([]);
  const [urunler, setUrunler] = useState([]);
  const [loading, setLoading] = useState(isEdit);
  const [loadingPdf, setLoadingPdf] = useState(false);

  const [selectedFirm, setSelectedFirm] = useState("");
  const [baslangicTarihi, setBaslangicTarihi] = useState(() => new Date().toISOString().split("T")[0]);
  const [kdvOrani, setKdvOrani] = useState(20);
  const [productList, setProductList] = useState([]);

  const [selectedProduct, setSelectedProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [unitPrice, setUnitPrice] = useState(0);
  const [birim, setBirim] = useState({ value: "1", label: "₺" });
  const [aciklama, setAciklama] = useState("");
  const [urunBaslangicTarihi, setUrunBaslangicTarihi] = useState(() => new Date().toISOString().slice(0, 10));
  const [editKey, setEditKey] = useState(null);

  const [filterMode, setFilterMode] = useState("all"); // all | active | passive
  const [expandedGroup, setExpandedGroup] = useState(null);
  const [selectedProducts, setSelectedProducts] = useState([]);

  const [showBulkReturn, setShowBulkReturn] = useState(false);
  const [topluIadeUrunler, setTopluIadeUrunler] = useState([]);
  const [bulkReturnIadeTarihi, setBulkReturnIadeTarihi] = useState("");
  const [bulkReturnIadeNotu, setBulkReturnIadeNotu] = useState("");

  const [showCariModal, setShowCariModal] = useState(false);

  const selectedFirmAdi = firms.find((f) => String(f.id) === String(selectedFirm))?.name || "";

  useEffect(() => {
    axios
      .get(`${nodeURL}/getFirmalar`)
      .then((r) => setFirms(r.data))
      .catch(() => toast.error("Firmalar yüklenirken hata oluştu!", { position: "top-right" }));
    axios
      .get(`${nodeURL}/getUrunler`)
      .then((r) => setUrunler(r.data))
      .catch(() => toast.error("Ürünler yüklenirken hata oluştu!", { position: "top-right" }));
  }, []);

  const reloadCurrentKiralama = () => {
    if (!isEdit) return;
    axios.get(`${nodeURL}/getKiralamalar`).then((res) => {
      const found = (res.data || []).find((k) => String(k.id) === String(kiralamaId));
      if (found) {
        setProductList((found.urunler || []).map(mapKiralamaUrun));
      }
    });
  };

  useEffect(() => {
    if (!isEdit) return;
    setLoading(true);
    axios
      .get(`${nodeURL}/getKiralamalar`)
      .then((res) => {
        const found = (res.data || []).find((k) => String(k.id) === String(kiralamaId));
        if (!found) {
          toast.error("Kiralama bulunamadı!", { position: "top-right" });
          navigate("/home/kiradakiurunler");
          return;
        }
        setSelectedFirm(found.firma_id);
        const [day, month, year] = found.baslangic_tarihi.split("-");
        setBaslangicTarihi(`${year}-${month}-${day}`);
        setKdvOrani(Number(found.kdv_orani));
        setProductList((found.urunler || []).map(mapKiralamaUrun));
      })
      .catch(() => toast.error("Kiralama detayı alınamadı!", { position: "top-right" }))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, kiralamaId]);

  // Teklif → kiralama dönüşümü (sadece yeni kiralama oluştururken)
  useEffect(() => {
    if (isEdit || !gelenTeklif) return;
    setTeklifInfo(gelenTeklif);
    const mapped = (gelenTeklif.urunler || []).map((u) => {
      const adet = Number(u.adet ?? 0);
      const birimFiyat = Number(u.birim_fiyati ?? 0);
      const toplam = Number(u.toplam ?? adet * birimFiyat);
      return {
        _localKey: newLocalKey(),
        urun_adi: u.urun_adi,
        urun_id: u.urun_id,
        urun_cinsi_id: u.urun_cinsi_id,
        adet,
        birim_fiyat: birimFiyat,
        birim: u.birim,
        toplam,
        aciklama: u.aciklama || "",
        firma_id: gelenTeklif.firma_id,
      };
    });
    setProductList(mapped);
    setSelectedFirm(gelenTeklif.firma_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, gelenTeklif]);

  const groupedProducts = productList.reduce((groups, p) => {
    const key = `${p.urun_id}_${p.urun_aciklama || p.aciklama || ""}`;
    if (!groups[key]) groups[key] = [];
    groups[key].push(p);
    return groups;
  }, {});

  const findUrunById = (urunId) => urunler.find((u) => Number(u.id) === Number(urunId));

  const getPendingAdetForUrun = (urunId) =>
    productList.reduce((sum, p) => {
      if (p.kiralama_urunler_id || p.iade_mi) return sum;
      if (Number(p.urun_id) !== Number(urunId)) return sum;
      if (editKey !== null && p._localKey === editKey) return sum;
      return sum + Number(p.adet || 0);
    }, 0);

  const getUrunStockInfo = (urunId) => {
    const urun = findUrunById(urunId);
    if (!urun) return { stok: 0, kirada: 0, kalan: 0 };
    const stok = Number(urun.stok_adet || 0);
    const kirada = Number(urun.kirada_adet || 0);
    const kalan = Number.isFinite(Number(urun.kalan_adet)) ? Number(urun.kalan_adet) : Math.max(stok - kirada, 0);
    return { stok, kirada, kalan };
  };

  const getAvailableForUrun = (urunId) => {
    const { kalan } = getUrunStockInfo(urunId);
    return Math.max(kalan - getPendingAdetForUrun(urunId), 0);
  };

  const onChangeBirim = (e) => {
    setBirim(birimler.find((b) => b.value === e.target.value) || { value: "", label: "" });
  };

  const handleAddProduct = () => {
    if (!selectedProduct || quantity <= 0 || unitPrice <= 0) {
      toast.error("Tüm alanları doldurun", { position: "top-right" });
      return;
    }
    if (Number(selectedProduct.cinsi_id) !== 1) {
      const available = getAvailableForUrun(selectedProduct.value);
      if (quantity > available) {
        toast.warning(`Stok yetersiz. Uygun adet: ${available}`, { position: "top-right" });
        return;
      }
    }
    const newProductData = {
      firma_id: selectedFirm,
      urun_id: selectedProduct.value,
      urun_adi: selectedProduct.label,
      urun_cinsi_id: selectedProduct.cinsi_id,
      adet: quantity,
      birim_fiyat: unitPrice,
      toplam: quantity * unitPrice,
      birim: birim.label,
      aciklama,
      eklenme_tarihi: isEdit ? urunBaslangicTarihi : undefined,
    };

    if (editKey !== null) {
      setProductList((prev) =>
        prev.map((p) => (p._localKey === editKey ? { ...p, ...newProductData, _localKey: p._localKey } : p))
      );
      setEditKey(null);
    } else {
      setProductList((prev) => [...prev, { ...newProductData, _localKey: newLocalKey() }]);
    }

    setSelectedProduct(null);
    setQuantity(1);
    setUnitPrice(0);
    setAciklama("");
    setUrunBaslangicTarihi(new Date().toISOString().slice(0, 10));
  };

  const handleEditProduct = (product) => {
    if (!product) return;
    const matchingUrun = findUrunById(product.urun_id);
    setSelectedProduct({
      value: product.urun_id,
      label: product.urun_adi,
      cinsi_id: product.urun_cinsi_id,
      stok_adet: matchingUrun?.stok_adet,
      kirada_adet: matchingUrun?.kirada_adet,
      kalan_adet: matchingUrun?.kalan_adet,
    });
    setQuantity(product.adet);
    setUnitPrice(product.birim_fiyat);
    setAciklama(product.aciklama);
    setUrunBaslangicTarihi(product.eklenme_tarihi || new Date().toISOString().slice(0, 10));
    setBirim({ value: product.birim_id || product.birim || 1, label: product.birim || "₺" });
    setEditKey(product._localKey);
  };

  const cancelEditProduct = () => {
    setEditKey(null);
    setSelectedProduct(null);
    setUnitPrice(0);
    setAciklama("");
    setUrunBaslangicTarihi(new Date().toISOString().slice(0, 10));
  };

  const handleDeleteProduct = (localKey) => {
    Swal.fire({
      title: "Emin misiniz?",
      text: "Bu ürünü silmek istediğinize emin misiniz?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Evet, sil",
      cancelButtonText: "Vazgeç",
    }).then((result) => {
      if (!result.isConfirmed) return;
      setProductList((prev) => prev.filter((p) => p._localKey !== localKey));
      if (editKey === localKey) cancelEditProduct();
      Swal.fire({ title: "Silindi", text: "Ürün başarıyla silindi.", icon: "success", timer: 1500, showConfirmButton: false });
    });
  };

  const handleiade = (urun) => {
    const diffDays = (a, b) => Math.floor((a.getTime() - b.getTime()) / (1000 * 60 * 60 * 24));
    const addDays = (d, days) => new Date(d.getTime() + days * 24 * 60 * 60 * 1000);
    const fmtTR = (d) => (d ? d.toLocaleDateString("tr-TR") : "-");

    Swal.fire({
      title: "Emin misiniz?",
      html: `
        <p>Ürünün İade Edildiğinden Emin Misiniz!</p>
        <p style="margin-top:8px; font-size: 13px; color:#555;">
          Bilgi: İade tarihi, son fatura/dönem başlangıcından itibaren <b>30 günü doldurduysa</b> o dönem ücreti alınır.
          30 gün dolmadan iade edilirse o dönemin borcu yansıtılmaz (iptal edilir).
        </p>
        <label for="iadeTarihi">İade Tarihi:</label>
        <input type="date" id="iadeTarihi" class="swal2-input" value="${new Date().toISOString().split("T")[0]}" />
        <label for="iadeNotu" style="margin-top:8px; display:block;">İade şantiyesi / not (opsiyonel):</label>
        <input type="text" id="iadeNotu" class="swal2-input" placeholder="Örn: Astoria AVM" />
      `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Evet",
      cancelButtonText: "Vazgeç",
      preConfirm: () => {
        const iadeTarihi = document.getElementById("iadeTarihi").value;
        if (!iadeTarihi) Swal.showValidationMessage("İade tarihi seçilmelidir!");
        const iadeNotu = document.getElementById("iadeNotu").value.trim();
        return { iadeTarihi, iadeNotu };
      },
    }).then((result) => {
      if (!result.isConfirmed) return;
      const kiralama_urunler_id = urun.kiralama_urunler_id;
      const { iadeTarihi, iadeNotu } = result.value;

      // eklenme_tarihi sunucudan zaten ISO (YYYY-MM-DD) geliyor — görüntü
      // metni olan baslangic_tarihi (DD.MM.YYYY) ayrıştırılmaya çalışılmıyor.
      const startDate = urun.eklenme_tarihi ? new Date(urun.eklenme_tarihi) : null;
      const returnDate = new Date(iadeTarihi);

      let infoHtml;
      if (startDate && !Number.isNaN(startDate.getTime())) {
        const totalDays = Math.max(0, diffDays(returnDate, startDate));
        const periodIndex = Math.floor(totalDays / 30);
        const periodStart = addDays(startDate, periodIndex * 30);
        const periodEnd = addDays(periodStart, 30);
        const daysSincePeriodStart = Math.max(0, diffDays(returnDate, periodStart));
        const willChargeThisPeriod = returnDate.getTime() >= periodEnd.getTime();

        infoHtml = `
          <div style="text-align:left; font-size: 14px;">
            <div><b>Son dönem başlangıcı:</b> ${fmtTR(periodStart)}</div>
            <div><b>İade tarihi:</b> ${fmtTR(returnDate)}</div>
            <div><b>Geçen süre:</b> ${daysSincePeriodStart} gün</div>
            <hr/>
            <div>
              ${
                willChargeThisPeriod
                  ? "<b>30 gün dolduğu için</b> bu dönem ücreti alınır (borç oluşmuş kabul edilir)."
                  : "<b>30 gün dolmadan iade edildiği için</b> bu dönemin borcu yansıtılmaz/iptal edilir."
              }
            </div>
          </div>
        `;
      } else {
        infoHtml = `<div style="text-align:left;">Bilgi: 30 gün dolduysa dönem ücreti alınır, dolmadıysa alınmaz.</div>`;
      }

      Swal.fire({
        title: "İade Bilgilendirmesi",
        html: infoHtml,
        icon: "info",
        showCancelButton: true,
        confirmButtonText: "Devam Et",
        cancelButtonText: "Vazgeç",
      }).then((confirm2) => {
        if (!confirm2.isConfirmed) return;
        axios
          .put(`${nodeURL}/iadeUrun/${kiralama_urunler_id}`, {
            iade_tarihi: iadeTarihi,
            iade_aciklama: iadeNotu || null,
          })
          .then(() => {
            toast.success("Ürün İade İşlemi Başarılı!", { position: "top-right" });
            reloadCurrentKiralama();
          })
          .catch(() => toast.error("İade sırasında hata oluştu!", { position: "top-right" }));
      });
    });
  };

  const handleSelectGroup = (groupKey, checked) => {
    const groupIds = groupedProducts[groupKey].map((p) => p.kiralama_urunler_id).filter(Boolean);
    setSelectedProducts((prev) =>
      checked ? [...new Set([...prev, ...groupIds])] : prev.filter((id) => !groupIds.includes(id))
    );
  };

  const handleSelectProduct = (id, checked) => {
    setSelectedProducts((prev) => (checked ? [...prev, id] : prev.filter((pid) => pid !== id)));
  };

  const handleOpenTopluIade = (selectedIds) => {
    if (!selectedIds || selectedIds.length === 0) {
      toast.warning("Lütfen en az bir ürün seçiniz.", { position: "top-right" });
      return;
    }
    setTopluIadeUrunler(selectedIds);
    setBulkReturnIadeTarihi(new Date().toISOString().slice(0, 10));
    setShowBulkReturn(true);
  };

  const handleSaveBulkReturn = () => {
    if (!bulkReturnIadeTarihi) {
      toast.warning("Lütfen iade tarihi seçiniz.", { position: "top-right" });
      return;
    }
    axios
      .put(`${nodeURL}/topluIadeUrun`, {
        urunler: topluIadeUrunler,
        iade_tarihi: bulkReturnIadeTarihi,
        iade_aciklama: bulkReturnIadeNotu.trim() || null,
      })
      .then(() => {
        toast.success("Toplu iade işlemi başarılı!", { position: "top-right" });
        setShowBulkReturn(false);
        setBulkReturnIadeNotu("");
        setSelectedProducts([]);
        reloadCurrentKiralama();
      })
      .catch(() => toast.error("Toplu iade sırasında hata oluştu!", { position: "top-right" }));
  };

  const handlePdfStokTakip = async () => {
    setLoadingPdf(true);
    try {
      const response = await axios.post(
        `${nodeURL}/stokPdfOlustur`,
        { firmaAdi: selectedFirmAdi, kiralamaTarihi: baslangicTarihi, productList },
        { responseType: "arraybuffer", headers: { "Content-Type": "application/json", Accept: "application/pdf" } }
      );
      const blob = new Blob([response.data], { type: "application/pdf" });
      window.open(window.URL.createObjectURL(blob));
    } catch (error) {
      toast.error("Stok PDF indirilemedi!", { position: "top-right" });
    } finally {
      setLoadingPdf(false);
    }
  };

  const handleGeriDon = () => {
    Swal.fire({
      title: "Emin misiniz?",
      text: "Kaydedilmemiş veriler silinecek!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Evet, geri dön",
      cancelButtonText: "Hayır, kal",
    }).then((result) => {
      if (result.isConfirmed) navigate("/home/kiradakiurunler");
    });
  };

  const toplamlar = useMemo(() => {
    const yeniEklenenToplam = productList
      .filter((p) => !p.kiralama_urunler_id && !p.iade_mi)
      .reduce((acc, p) => acc + (p.toplam || p.birim_fiyat * p.adet || 0), 0);
    const araToplam = productList
      .filter((p) => !p.iade_mi)
      .reduce((acc, p) => acc + (p.toplam || p.birim_fiyat * p.adet || 0), 0);
    const kdvTutar = araToplam * (kdvOrani / 100);
    const genelToplam = araToplam + kdvTutar;
    return { yeniEklenenToplam, araToplam, kdvTutar, genelToplam };
  }, [productList, kdvOrani]);

  const handleSaveHizmet = async () => {
    if (!selectedFirm) {
      toast.error("Lütfen bir firma seçiniz!", { position: "top-right" });
      return;
    }
    if (productList.length === 0) {
      toast.error("Ürün/Hizmet Bilgileri Girilmeden Hizmet Başlatılamaz!", { position: "top-right" });
      return;
    }
    setLoading(true);
    const dataToSend = {
      firmaId: selectedFirm,
      urunler: productList,
      araToplam: toplamlar.araToplam,
      kdvOrani,
      kdvTutar: toplamlar.kdvTutar,
      genelToplam: toplamlar.genelToplam,
      baslangicTarihi,
      gelenTeklifId: gelenTeklif?.id ?? null,
    };
    try {
      await axios.post(`${nodeURL}/kiralamaBaslat`, dataToSend);
      toast.success("Kaydetme Başarılı!", { position: "top-right" });
      navigate("/home/kiradakiurunler");
    } catch (error) {
      const detail = error.response?.data?.error || error.response?.data?.msg;
      toast.error(detail || "Kayıt sırasında bir hata oluştu!", { position: "top-right" });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    setLoading(true);
    const dataToSend = {
      kiralamaId,
      firmaId: selectedFirm,
      urunler: productList,
      araToplam: toplamlar.araToplam,
      kdvOrani,
      kdvTutar: toplamlar.kdvTutar,
      genelToplam: toplamlar.genelToplam,
    };
    try {
      await axios.post(`${nodeURL}/kiralamaGuncelle`, dataToSend);
      toast.success("Güncelleme İşlemi Başarılı!", { position: "top-right" });
      navigate("/home/kiradakiurunler");
    } catch (error) {
      const detail = error.response?.data?.error || error.response?.data?.msg;
      toast.error(detail || "Güncelleme sırasında hata oluştu!", { position: "top-right" });
    } finally {
      setLoading(false);
    }
  };

  const filteredGroupedProducts = Object.entries(groupedProducts).reduce((acc, [groupKey, items]) => {
    let filteredItems = items;
    if (filterMode === "active") filteredItems = items.filter((p) => !p.iade_mi);
    else if (filterMode === "passive") filteredItems = items.filter((p) => p.iade_mi);
    if (filteredItems.length > 0) acc[groupKey] = filteredItems;
    return acc;
  }, {});

  const renderActionButtons = (p) => (
    <div className="d-flex gap-1 flex-wrap">
      <button className="btn btn-sm btn-outline-secondary" onClick={(e) => { e.stopPropagation(); handleEditProduct(p); }} title="Düzenle">
        <CiEdit size="18px" />
      </button>
      {!p.kiralama_urunler_id && (
        <button className="btn btn-sm btn-outline-danger" onClick={(e) => { e.stopPropagation(); handleDeleteProduct(p._localKey); }} title="Sil">
          <CiTrash size="18px" />
        </button>
      )}
      {p.kiralama_urunler_id && !p.iade_mi && (
        <button className="btn btn-sm btn-outline-warning" onClick={(e) => { e.stopPropagation(); handleiade(p); }}>
          Ürün İade
        </button>
      )}
    </div>
  );

  return (
    <div
      style={{
        background: "rgba(255, 255, 255, 0.2)",
        borderRadius: "15px",
        padding: "20px",
        backdropFilter: "blur(10px)",
        boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.2)",
      }}
      className="container-fluid"
    >
      <div className="d-flex align-items-center justify-content-between mb-3">
        <button className="btn btn-outline-secondary" onClick={handleGeriDon}>
          <CiSquareChevLeft /> Geri Dön
        </button>
        {teklifInfo && (
          <h4 className="m-0 text-center flex-grow-1">Teklif: {teklifInfo?.proje_ismi}</h4>
        )}
        <div style={{ width: "120px" }}></div>
      </div>

      {loading && !productList.length && isEdit ? (
        <div style={{ textAlign: "center", padding: "20px" }}>Yükleniyor...</div>
      ) : (
        <>
          <div className="row mb-3 justify-content-center">
            <div className="col-12 col-md-4">
              <label className="form-label">Firma Seç:</label>
              <select
                className="form-input form-select"
                value={selectedFirm}
                disabled={isEdit}
                onChange={(e) => setSelectedFirm(e.target.value)}
              >
                <option value="">Seçiniz</option>
                {firms.map((d, i) => (
                  <option key={i} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-12 col-md-2">
              <label className="form-label">Hizmet Başlangıcı:</label>
              <input
                type="date"
                disabled={isEdit}
                className="form-control form-input"
                value={baslangicTarihi}
                onChange={(e) => setBaslangicTarihi(e.target.value)}
              />
            </div>
          </div>

          {/* Ürün Ekleme */}
          <div style={{ marginTop: "10px", marginLeft: "12%" }}>
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <div className="d-flex flex-column col-12 col-md-2">
                <label className="form-label">Ürün/Hizmet Ekle</label>
                <select
                  className="form-input form-select"
                  value={selectedProduct ? selectedProduct.value : ""}
                  onChange={(e) => {
                    const selectedId = e.target.value;
                    const selectedItem = urunler.find((u) => u.id.toString() === selectedId);
                    if (selectedItem) {
                      setSelectedProduct({
                        value: selectedItem.id,
                        label: selectedItem.adi,
                        cinsi_id: selectedItem.cinsi_id,
                        stok_adet: selectedItem.stok_adet,
                        kirada_adet: selectedItem.kirada_adet,
                        kalan_adet: selectedItem.kalan_adet,
                      });
                    }
                  }}
                >
                  <option value="">Ürün Seç</option>
                  {urunler.map((u, i) => {
                    const stok = Number(u.stok_adet || 0);
                    const kirada = Number(u.kirada_adet || 0);
                    const kalan = Number.isFinite(Number(u.kalan_adet)) ? Number(u.kalan_adet) : Math.max(stok - kirada, 0);
                    const isService = Number(u.cinsi_id) === 1;
                    return (
                      <option key={i} value={u.id}>
                        {isService ? u.adi : `${u.adi} (Stok: ${stok}, Kirada: ${kirada}, Kalan: ${kalan})`}
                      </option>
                    );
                  })}
                </select>
                {selectedProduct && Number(selectedProduct.cinsi_id) !== 1 && (
                  <small style={{ color: "#555" }}>
                    Stok: {getUrunStockInfo(selectedProduct.value).stok} | Kirada: {getUrunStockInfo(selectedProduct.value).kirada} | Kalan:{" "}
                    {getUrunStockInfo(selectedProduct.value).kalan} | Uygun: {getAvailableForUrun(selectedProduct.value)}
                  </small>
                )}
              </div>
              <div className="d-flex flex-column">
                <label className="form-label">Adet</label>
                <input
                  className="form-input form-control"
                  type="number"
                  placeholder="Adet"
                  value={quantity || ""}
                  onChange={(e) => setQuantity(Number(e.target.value))}
                  max={selectedProduct && Number(selectedProduct.cinsi_id) !== 1 ? getAvailableForUrun(selectedProduct.value) : undefined}
                />
              </div>
              <div className="d-flex flex-column">
                <label className="form-label">Fiyatı (Adet)</label>
                <input
                  className="form-input form-control"
                  type="number"
                  placeholder="Birim Fiyat"
                  value={unitPrice || ""}
                  onChange={(e) => setUnitPrice(Number(e.target.value))}
                  min="1"
                />
              </div>
              <div className="d-flex flex-column">
                <label className="form-label">Birim</label>
                <select className="form-input form-select" value={birim.value} onChange={onChangeBirim} style={{ width: "80px" }}>
                  {birimler.map((b) => (
                    <option key={b.value} value={b.value}>
                      {b.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="d-flex flex-column">
                <label className="form-label">Tutar</label>
                <input
                  disabled
                  className="form-input form-control"
                  type="number"
                  value={quantity * unitPrice}
                  readOnly
                  style={{ width: "150px" }}
                  placeholder="Toplam"
                />
              </div>
              <div className="d-flex flex-column">
                <label className="form-label">Açıklama</label>
                <input
                  className="form-input form-control"
                  type="text"
                  placeholder="..."
                  value={aciklama || ""}
                  style={{ width: "25vh" }}
                  onChange={(e) => setAciklama(e.target.value)}
                />
              </div>
              {isEdit && (
                <div className="d-flex flex-column">
                  <label className="form-label">Kiralama Tarihi</label>
                  <input
                    className="form-input form-control"
                    type="date"
                    value={urunBaslangicTarihi}
                    onChange={(e) => setUrunBaslangicTarihi(e.target.value)}
                    style={{ width: "150px" }}
                  />
                </div>
              )}
              <div className="d-flex flex-column mt-3">
                <button className="btn btn-success btn-sm" onClick={handleAddProduct}>
                  {editKey !== null ? "Güncelle" : "Ekle"}
                </button>
                {editKey !== null && (
                  <button className="btn btn-outline-secondary btn-sm mt-1" onClick={cancelEditProduct}>
                    İptal
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="mb-3 mt-3 d-flex justify-content-center">
            <div className="form-check form-check-inline">
              <input className="form-check-input" type="radio" name="filter" id="filterAll" value="all" checked={filterMode === "all"} onChange={(e) => setFilterMode(e.target.value)} />
              <label className="form-check-label fw-bold" htmlFor="filterAll">Tümü</label>
            </div>
            <div className="form-check form-check-inline">
              <input className="form-check-input" type="radio" name="filter" id="filterActive" value="active" checked={filterMode === "active"} onChange={(e) => setFilterMode(e.target.value)} />
              <label className="form-check-label fw-bold" htmlFor="filterActive">Aktifler</label>
            </div>
            <div className="form-check form-check-inline">
              <input className="form-check-input" type="radio" name="filter" id="filterPassive" value="passive" checked={filterMode === "passive"} onChange={(e) => setFilterMode(e.target.value)} />
              <label className="form-check-label fw-bold" htmlFor="filterPassive">Pasifler</label>
            </div>
          </div>

          <div style={{ maxHeight: "500px", overflowY: "auto" }} className="table-responsive mt-3">
            <table className="table table-bordered table-hover">
              <thead>
                <tr>
                  <th style={{ width: "32px", textAlign: "center" }}>Seç</th>
                  <th>Ürün</th>
                  <th>Adet</th>
                  <th>Birim Fiyat</th>
                  <th>Açıklama</th>
                  <th style={{ width: "16%" }}>
                    <div className="d-flex align-items-center justify-content-between">
                      <span>İşlem</span>
                      <button className="btn btn-sm btn-outline-danger" onClick={handlePdfStokTakip} disabled={loadingPdf} title="Ürün Listesi PDF">
                        {loadingPdf ? <FaSpinner className="fa-spin" /> : <FaRegFileLines />}
                      </button>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(filteredGroupedProducts).map(([groupKey, items]) => {
                  const first = items[0];
                  const isSingle = items.length === 1;
                  const groupIds = items.map((p) => p.kiralama_urunler_id).filter(Boolean);
                  const allSelected = groupIds.length > 0 && groupIds.every((id) => selectedProducts.includes(id));
                  const someSelected = groupIds.some((id) => selectedProducts.includes(id));

                  return (
                    <React.Fragment key={groupKey}>
                      <tr
                        onClick={() => !isSingle && setExpandedGroup((prev) => (prev === groupKey ? null : groupKey))}
                        style={{
                          backgroundColor: expandedGroup === groupKey ? "#e9ecef" : "transparent",
                          cursor: isSingle ? "default" : "pointer",
                          opacity: expandedGroup && expandedGroup !== groupKey ? 0.5 : 1,
                          transition: "opacity 0.2s ease",
                        }}
                      >
                        <td style={{ textAlign: "center" }} onClick={(e) => e.stopPropagation()}>
                          {!isSingle && expandedGroup === groupKey && groupIds.length > 0 && (
                            <input
                              type="checkbox"
                              checked={allSelected}
                              ref={(el) => { if (el) el.indeterminate = someSelected && !allSelected; }}
                              onChange={(e) => handleSelectGroup(groupKey, e.target.checked)}
                            />
                          )}
                        </td>
                        <td>{first.urun_adi}</td>
                        <td>{items.reduce((sum, p) => sum + (p.adet || 0), 0)}</td>
                        <td>{first.birim_fiyat ?? "-"}</td>
                        <td>{first.urun_aciklama || first.aciklama || "-"}</td>
                        <td>
                          {isSingle && renderActionButtons(first)}
                          {!isSingle && groupIds.filter((id) => selectedProducts.includes(id)).length >= 2 && (
                            <button
                              className="btn btn-sm btn-warning"
                              onClick={(e) => { e.stopPropagation(); handleOpenTopluIade(groupIds.filter((id) => selectedProducts.includes(id))); }}
                            >
                              Toplu İade Yap
                            </button>
                          )}
                        </td>
                      </tr>

                      {!isSingle && expandedGroup === groupKey && items.map((p) => (
                        <tr key={p._localKey} style={{ borderLeft: "4px solid #7f7f7f" }}>
                          <td style={{ textAlign: "center" }}>
                            {p.kiralama_urunler_id && (
                              <input
                                type="checkbox"
                                checked={selectedProducts.includes(p.kiralama_urunler_id)}
                                onChange={(e) => handleSelectProduct(p.kiralama_urunler_id, e.target.checked)}
                              />
                            )}
                          </td>
                          <td>
                            {p.urun_adi}
                            {p.iade_mi && (
                              <span style={{ fontWeight: "bold", marginLeft: "8px", color: "#d9534f" }}>
                                - İade Edildi ({new Date(p.iade_tarihi).toLocaleDateString("tr-TR")})
                              </span>
                            )}
                            {p.iade_mi && p.iade_aciklama && (
                              <span style={{ marginLeft: "8px", color: "#555" }}>· Not: {p.iade_aciklama}</span>
                            )}
                          </td>
                          <td>{p.adet}</td>
                          <td>{p.birim_fiyat}</td>
                          <td>{p.urun_aciklama || p.aciklama}</td>
                          <td>{renderActionButtons(p)}</td>
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {showBulkReturn && createPortal(
            // Sayfanın dış kutusundaki backdropFilter (blur), position:fixed
            // için yeni bir konumlanma bağlamı açtığından modal document.body'ye
            // portal ile render ediliyor (aksi halde uzun listede sayfanın
            // üstüne sabitlenip scroll gerektirebiliyordu).
            <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
              <div className="modal-dialog modal-lg modal-fullscreen-sm-down">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">Toplu İade</h5>
                    <button type="button" className="btn-close" onClick={() => setShowBulkReturn(false)}></button>
                  </div>
                  <div className="modal-body">
                    <p>Seçilen ürün sayısı: <strong>{topluIadeUrunler.length}</strong></p>
                    <div className="mb-2">
                      <label>İade Tarihi</label>
                      <input
                        type="date"
                        className="form-control"
                        value={bulkReturnIadeTarihi}
                        onChange={(e) => setBulkReturnIadeTarihi(e.target.value)}
                      />
                    </div>
                    <div className="mb-2">
                      <label>İade şantiyesi / not (opsiyonel)</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Örn: Astoria AVM"
                        value={bulkReturnIadeNotu}
                        onChange={(e) => setBulkReturnIadeNotu(e.target.value)}
                      />
                    </div>
                    <p>*Seçilen bütün ürünler iade edilecektir.</p>
                  </div>
                  <div className="modal-footer">
                    <button className="btn btn-secondary" onClick={() => setShowBulkReturn(false)}>Vazgeç</button>
                    <button className="btn btn-warning" onClick={handleSaveBulkReturn}>İade Et</button>
                  </div>
                </div>
              </div>
            </div>,
            document.body
          )}

          <div className="mt-3" style={{ maxWidth: "500px", marginLeft: "auto" }}>
            <h5 className="mb-3 text-center">Özet</h5>

            <div className="mb-2 d-flex justify-content-between">
              <span><strong>Yeni Eklenen Ürünler:</strong></span>
              <span>{toplamlar.yeniEklenenToplam.toFixed(2)} ₺</span>
            </div>
            <div className="mb-2 d-flex justify-content-between">
              <span><strong>Ara Toplam:</strong></span>
              <span>{toplamlar.araToplam.toFixed(2)} ₺</span>
            </div>
            <div className="mb-2 d-flex justify-content-between">
              <span><strong>KDV Tutarı ({kdvOrani}%):</strong></span>
              <span>{toplamlar.kdvTutar.toFixed(2)} ₺</span>
            </div>
            <div className="mb-3 d-flex justify-content-between">
              <span><strong>Genel Toplam:</strong></span>
              <span>{toplamlar.genelToplam.toFixed(2)} ₺</span>
            </div>

            {isEdit && (
              <div className="d-flex gap-2 mb-2">
                <button className="btn btn-outline-success w-100" type="button" onClick={() => setShowCariModal(true)}>
                  Firma Ödemesi / Cari
                </button>
              </div>
            )}

            <button disabled={loading} className="btn btn-primary w-100" onClick={isEdit ? handleUpdate : handleSaveHizmet}>
              {isEdit ? "Güncelle" : "Hizmet Başlat"}
              {loading && <span className="spinner-border spinner-border-sm ms-2" role="status" aria-hidden="true"></span>}
            </button>
          </div>

          <FirmaCariModal
            show={showCariModal && isEdit}
            onClose={() => setShowCariModal(false)}
            firmaId={selectedFirm}
            firmaAdi={selectedFirmAdi}
          />
        </>
      )}
    </div>
  );
};

export default KiralamaEditor;
